import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
// import WhatsAppButton from "./WhatsAppButton";
import DemoButton from "./DemoButton";

const Layout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: "Home", href: "/" },
    { name: "Courses", href: "/#courses", isAnchor: true },
    // { name: "Test Your Level", href: "/test" },
    { name: "Blogs", href: "/blogs" },
    { name: "About", href: "/about" },
  ];

  const handleCoursesClick = (e) => {
    if (location.pathname === "/") {
      // If we're on homepage, scroll to courses section
      e.preventDefault();
      const coursesSection = document.getElementById("courses");
      if (coursesSection) {
        coursesSection.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If we're on another page, navigate to homepage then scroll
      window.location.href = "/#courses";
    }
    setMobileMenuOpen(false);
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  // Helper function to determine if a nav item is active
  const isActiveNavItem = (item) => {
    if (item.isAnchor) {
      // For Courses link, show as active when on homepage
      return location.pathname === "/";
    }
    return location.pathname === item.href;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center"
                onClick={() => setMobileMenuOpen(false)}
              >
                <img
                  src="/images/logo.webp"
                  alt=""
                  className="h-12 w-auto mr-3"
                />
                <span className="text-xl font-bold text-gray-900">
                  English Tunnels
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              {navigation.map((item) =>
                item.isAnchor ? (
                  <button
                    key={item.name}
                    onClick={handleCoursesClick}
                    className={`text-gray-600 hover:text-primary-600 transition-colors duration-200 ${
                      isActiveNavItem(item)
                        ? "text-primary-600 font-semibold"
                        : ""
                    }`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`text-gray-600 hover:text-primary-600 transition-colors duration-200 ${
                      isActiveNavItem(item)
                        ? "text-primary-600 font-semibold"
                        : ""
                    }`}
                  >
                    {item.name}
                  </Link>
                ),
              )}
              <Link
                to="/test"
                className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200"
              >
                Start Free Test
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 p-2"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 bg-white">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {navigation.map((item) =>
                  item.isAnchor ? (
                    <button
                      key={item.name}
                      onClick={handleCoursesClick}
                      className={`block w-full text-left px-3 py-2 text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 ${
                        isActiveNavItem(item)
                          ? "text-primary-600 font-semibold bg-primary-50"
                          : ""
                      }`}
                    >
                      {item.name}
                    </button>
                  ) : (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={handleNavClick}
                      className={`block px-3 py-2 text-gray-600 hover:text-primary-600 hover:bg-gray-50 transition-colors duration-200 ${
                        isActiveNavItem(item)
                          ? "text-primary-600 font-semibold bg-primary-50"
                          : ""
                      }`}
                    >
                      {item.name}
                    </Link>
                  ),
                )}
                <Link
                  to="/test"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block mx-3 my-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors duration-200 text-center"
                >
                  Start Free Test
                </Link>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white pb-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-4">
                <img
                  src="/images/logo.webp"
                  alt=""
                  className="h-20 w-auto mr-3"
                />
                <span className="text-xl font-bold">English Tunnels</span>
              </div>
              <p className="text-gray-400 mb-4">
                Transform your English skills with our comprehensive online
                courses. Learn from expert instructors and join thousands of
                successful students.
              </p>
              <div className="flex space-x-4">
                <a
                  href="https://www.instagram.com/englishtunnels"
                  aria-label="Instagram"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-pink-500 transition"
                >
                  <svg
                    className="w-6 h-6"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2zm0 1.5A4.25 4.25 0 0 0 3.5 7.75v8.5A4.25 4.25 0 0 0 7.75 20.5h8.5a4.25 4.25 0 0 0 4.25-4.25v-8.5A4.25 4.25 0 0 0 16.25 3.5h-8.5zm4.25 3a5.75 5.75 0 1 1 0 11.5 5.75 5.75 0 0 1 0-11.5zm0 1.5a4.25 4.25 0 1 0 0 8.5 4.25 4.25 0 0 0 0-8.5zm5.75-.88a1.13 1.13 0 1 1-2.26 0 1.13 1.13 0 0 1 2.26 0z" />
                  </svg>
                </a>

                <a
                  href="https://www.linkedin.com/company/english-tunnels/"
                  aria-label="LinkedIn"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition"
                >
                  <svg
                    className="w-6 h-6"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/profile.php?id=61582405224424"
                  aria-label="Facebook"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-blue-600 transition"
                >
                  <svg
                    className="w-6 h-6"
                    aria-hidden="true"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M22.675 0h-21.35C.595 0 0 .593 0 1.326v21.348C0 23.406.595 24 1.325 24H12.82v-9.294H9.692V11.01h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.466.099 2.797.143v3.24h-1.918c-1.504 0-1.796.715-1.796 1.763v2.31h3.587l-.467 3.696h-3.12V24h6.116c.73 0 1.325-.594 1.325-1.326V1.326C24 .593 23.405 0 22.675 0z" />
                  </svg>
                </a>
              </div>
            </div>
            {/* quick links */}
            {/* <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/test"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Level Test
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <button
                    onClick={handleCoursesClick}
                    className="text-gray-400 hover:text-white transition-colors"
                  >
                    Courses
                  </button>
                </li>
              </ul>
            </div> */}

            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-gray-400">
                <li>Email: info@englishtunnels.com</li>
                <li>Phone: +91 74110-61494</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2025 English Tunnels. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Demo Button */}
      <DemoButton />

      {/* WhatsApp Button */}
      {/* <WhatsAppButton /> */}
    </div>
  );
};

export default Layout;
